// File: frontend/src/components/marketing/ProductShowcaseTabs.js
//
// The product tour (#product): Dashboard → Router → Vouchers → Analytics →
// Wallet, each a live marketing visual (fictional data, no API calls).
//
// ── SCROLL STORY ────────────────────────────────────────────────────────────
//
// On a wide, tall screen with motion allowed (STORY_MQ) and JavaScript
// running (html.js), the section is several viewports tall and its content is
// CSS-sticky under the header. A ScrollTrigger maps scroll progress to the
// active tab, so scrolling walks through the five parts and the page carries
// on after Wallet. Nothing is pinned or hijacked: it is ordinary scrolling
// past a sticky frame, in both directions.
//
// The tabs stay real tabs. Choosing one switches at once and scrolls the page
// to that tab's slot, so the scroll position and the tab agree afterwards;
// scroll updates are ignored while that scroll is under way.
//
// Everywhere else (phones, short screens, reduced motion, no JavaScript) it is
// a normal tabbed section.
//
// ── RENDERING ───────────────────────────────────────────────────────────────
//
// All five panels are in the markup (the static HTML carries every
// description). They share one grid cell, so switching never changes the
// height. Inactive panels start hidden through their initial inline style;
// from then on GSAP owns their opacity and visibility, and React never
// rewrites those values.

import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, Tabs, Tab, Stack, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { MarketingBrowserFrame } from "./visuals/primitives";
import MarketingDashboardVisual from "./visuals/MarketingDashboardVisual";
import MarketingRouterVisual from "./visuals/MarketingRouterVisual";
import MarketingVoucherVisual from "./visuals/MarketingVoucherVisual";
import MarketingAnalyticsVisual from "./visuals/MarketingAnalyticsVisual";
import MarketingWalletVisual from "./visuals/MarketingWalletVisual";
import {
  gsap, useGSAP, ScrollTrigger, MQ, STORY_MQ, HEADER_H, prefersReducedMotion,
} from "./landingMotion";

function DenseDashboard() {
  return <MarketingDashboardVisual dense />;
}

const TABS = [
  {
    key: "dashboard", label: "Dashboard", url: "pesafii.net/dashboard",
    title: "One dashboard for the whole business",
    desc: "Revenue, router status, active users and recent activity in a single connected view.",
    points: ["Live revenue & sales", "Router health at a glance", "Recent customer activity"],
    Visual: DenseDashboard,
  },
  {
    key: "routers", label: "Router", url: "pesafii.net/router",
    title: "Monitor every router in real time",
    desc: "Per-router status, uptime, connected users and resource usage, so you know which locations are performing.",
    points: ["Uptime & status", "CPU / memory / data usage", "Connected users"],
    Visual: MarketingRouterVisual,
  },
  {
    key: "vouchers", label: "Vouchers", url: "pesafii.net/vouchers",
    title: "Create, share and track vouchers",
    desc: "Generate voucher batches, watch redemptions update, and share printable or digital codes.",
    points: ["Batch generation", "Available / redeemed / expired", "Print & share"],
    Visual: MarketingVoucherVisual,
  },
  {
    key: "analytics", label: "Analytics", url: "pesafii.net/analytics",
    title: "Understand usage and demand",
    desc: "Sessions, data consumption, peak hours and usage by router to guide pricing and capacity.",
    points: ["Peak-hour demand", "Data consumption", "Usage by router"],
    Visual: MarketingAnalyticsVisual,
  },
  {
    key: "wallet", label: "Wallet", url: "pesafii.net/wallet",
    title: "Keep collections reconciled",
    desc: "Available balance, today's collections and a clear transaction history from payment to payout.",
    points: ["Available balance", "Transaction history", "Reconciled collections"],
    Visual: MarketingWalletVisual,
  },
];

// Also used as a React style prop, which React freezes: GSAP always gets a
// copy ({ ...HIDDEN }), because gsap.set writes into the object it is given.
const HIDDEN = { visibility: "hidden", opacity: 0 };
const initialStyle = (i) => (i === 0 ? undefined : { ...HIDDEN });

// Scroll distance per part in story mode. Five parts at 52svh made this section
// alone three full viewports, and with the payment story also becoming
// scroll-driven the page would have run to about fifteen. 42svh keeps every
// part comfortably long enough to read and brings the section to ~2.5.
const PER_TAB = "42svh";

export default function ProductShowcaseTabs() {
  const [tab, setTab] = useState(0);
  const root = useRef(null);
  const shown = useRef(0);        // the panel currently on screen
  const story = useRef(null);     // the story ScrollTrigger while story mode is on
  const lockUntil = useRef(0);

  const { contextSafe } = useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ story: STORY_MQ, always: MQ.always }, (ctx) => {
        // The tall layout is CSS behind html.js; without it there is no story.
        if (!ctx.conditions.story || !document.documentElement.classList.contains("js")) return undefined;
        const el = root.current;
        const start = `top top+=${HEADER_H}`;
        story.current = ScrollTrigger.create({
          trigger: el,
          start,
          end: "bottom bottom",
          onUpdate: (self) => {
            if (Date.now() < lockUntil.current) return;
            const i = Math.min(TABS.length - 1, Math.floor(self.progress * TABS.length));
            setTab((prev) => (prev === i ? prev : i));
          },
        });
        gsap.fromTo(el.querySelector("[data-story-progress]"), { scaleX: 0 }, {
          scaleX: 1, ease: "none",
          scrollTrigger: { trigger: el, start, end: "bottom bottom", scrub: true },
        });
        return () => { story.current = null; };
      });
    },
    { scope: root, dependencies: [] }
  );

  const swap = contextSafe((from, to) => {
    const q = gsap.utils.selector(root);
    const layers = (i) => [q(`[data-visual="${i}"]`)[0], q(`[data-copy="${i}"]`)[0]];
    const [outV, outC] = layers(from);
    const [inV, inC] = layers(to);
    const others = TABS.map((_, i) => i).filter((i) => i !== from && i !== to).flatMap(layers);
    gsap.killTweensOf(TABS.flatMap((_, i) => layers(i)));
    gsap.set(others, { ...HIDDEN });

    if (prefersReducedMotion()) {
      gsap.set([outV, outC], { ...HIDDEN });
      gsap.set([inV, inC], { visibility: "visible", opacity: 1, y: 0, scale: 1 });
      return;
    }
    const dir = to > from ? 1 : -1;
    gsap.to(outV, { autoAlpha: 0, y: -18 * dir, scale: 0.985, duration: 0.3, ease: "power2.in" });
    gsap.to(outC, { autoAlpha: 0, y: -10 * dir, duration: 0.25, ease: "power2.in" });
    gsap.fromTo(inV, { autoAlpha: 0, y: 26 * dir, scale: 0.985 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.55, delay: 0.14, ease: "power3.out" });
    gsap.fromTo(inC, { autoAlpha: 0, y: 14 * dir }, { autoAlpha: 1, y: 0, duration: 0.45, delay: 0.18, ease: "power3.out" });
  });

  useEffect(() => {
    if (shown.current === tab) return;
    swap(shown.current, tab);
    shown.current = tab;
  }, [tab, swap]);

  const select = (i) => {
    setTab(i);
    const st = story.current;
    if (!st) return;
    // Scroll to the middle of this tab's slot, and ignore scroll updates until
    // that scroll has finished.
    const top = st.start + (st.end - st.start) * ((i + 0.5) / TABS.length);
    lockUntil.current = Date.now() + 1200;
    window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };

  const storyCss = `@media ${STORY_MQ}`;

  return (
    <Box
      component="section"
      id="product"
      ref={root}
      aria-labelledby="product-title"
      sx={{
        scrollMarginTop: HEADER_H,
        position: "relative",
        [storyCss]: {
          "html.js &": { height: `calc(100svh - ${HEADER_H}px + ${TABS.length - 1} * ${PER_TAB})` },
          "html.js & [data-story-sticky]": {
            position: "sticky", top: HEADER_H, height: `calc(100svh - ${HEADER_H}px)`,
            display: "flex", alignItems: "center", py: 0,
          },
          "html.js & [data-story-only]": { display: "flex" },
        },
      }}
    >
      <Box data-story-sticky="" sx={{ py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg" sx={{ minWidth: 0 }}>
          <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: { xs: 2.5, md: 3 } }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mb: 1 }}>
              Product tour
            </Typography>
            <Typography id="product-title" component="h2" sx={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", lineHeight: 1.18, fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary" }}>
              See PesaFii at work
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: { xs: 3, md: 4 } }}>
            <Tabs
              value={tab}
              onChange={(_, v) => select(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Parts of PesaFii"
              sx={{
                minHeight: 0, maxWidth: "100%",
                "& .MuiTabs-indicator": { height: 3, borderRadius: 99, bgcolor: "primary.main" },
                "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 700, fontSize: 14.5, color: "text.secondary", "&.Mui-selected": { color: "text.primary" } },
              }}
            >
              {TABS.map((t, i) => (
                <Tab key={t.key} label={t.label} id={`product-tab-${i}`} aria-controls={`product-panel-${i}`} />
              ))}
            </Tabs>
            {/* Story mode only: how far through the tour the reader is. */}
            <Stack data-story-only="" aria-hidden="true" direction="row" alignItems="center" spacing={1.5}
              sx={{ display: "none", mt: 1.5, width: "min(420px, 100%)" }}>
              <Box sx={(t) => ({ flex: 1, height: 2, borderRadius: 99, bgcolor: alpha(t.palette.text.primary, 0.1), overflow: "hidden" })}>
                <Box data-story-progress="" sx={{ height: "100%", bgcolor: "primary.main", transformOrigin: "left center", transform: "scaleX(0)" }} />
              </Box>
              <Typography sx={{ fontSize: 11.5, color: "text.disabled", whiteSpace: "nowrap" }}>Scroll to explore</Typography>
            </Stack>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" }, gap: { xs: 3, md: 6 }, alignItems: "center" }}>
            {/* Copy panels */}
            <Box sx={{ minWidth: 0, order: { xs: 1, md: 0 }, display: "grid", "& > *": { gridArea: "1 / 1" } }}>
              {TABS.map((t, i) => (
                <Box
                  key={t.key}
                  data-copy={i}
                  role="tabpanel"
                  id={`product-panel-${i}`}
                  aria-labelledby={`product-tab-${i}`}
                  aria-hidden={i !== tab}
                  inert={i !== tab}
                  style={initialStyle(i)}
                >
                  <Typography component="h3" sx={{ fontSize: { xs: "1.35rem", md: "1.8rem" }, fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 1.25, lineHeight: 1.2 }}>
                    {t.title}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 14.5, md: 16 }, color: "text.secondary", lineHeight: 1.6, mb: 2 }}>
                    {t.desc}
                  </Typography>
                  <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
                    {t.points.map((p) => (
                      <Stack component="li" key={p} direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "primary.main", color: "primary.contrastText" }}>
                          <CheckIcon aria-hidden="true" sx={{ fontSize: 13 }} />
                        </Box>
                        <Typography sx={{ fontSize: 14.5, color: "text.primary", fontWeight: 600 }}>{p}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>

            {/* Visual layers (illustrations of the panels above).
                ONE browser frame, hoisted outside the swapped layers: the
                chrome and the address bar belong to the tour, not to a tab, so
                only what is inside the window changes. With a frame per tab the
                whole window faded out and back on every step, which read as
                five different screenshots rather than one product. All five
                layers share a grid cell, so the frame's height is the tallest
                of them and never changes as tabs switch. */}
            <Box aria-hidden="true" sx={{ minWidth: 0, order: { xs: 0, md: 1 } }}>
              <MarketingBrowserFrame url={TABS[tab].url}>
                <Box sx={{ display: "grid", alignItems: "center", minWidth: 0, "& > *": { gridArea: "1 / 1" } }}>
                  {TABS.map((t, i) => {
                    const Visual = t.Visual;
                    return (
                      <Box key={t.key} data-visual={i} style={initialStyle(i)} sx={{ minWidth: 0 }}>
                        <Visual />
                      </Box>
                    );
                  })}
                </Box>
              </MarketingBrowserFrame>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
